import { forwardRef, useId } from 'react';
import type { ReactNode } from 'react';

import { cn } from 'components/utils';

import {
  contentBlockContentRecipe,
  contentBlockDescriptionRecipe,
  contentBlockFooterRecipe,
  contentBlockHeaderRecipe,
  contentBlockRootRecipe,
  contentBlockTitleRecipe,
} from './ContentBlock.recipe';

import type { ContentBlockProps } from './types';

/**
 * Whether a node would render nothing at all, so its wrapper can be left out entirely.
 *
 * The problem this solves is that `footer && <div/>` is not enough. A caller writes
 * `footer={isPublished && <PublishRow/>}`, the condition is false, and React receives `false` —
 * which renders nothing, but a `footer !== undefined` check has already rendered the wrapper
 * around it. The result is a 12px gap under the body with nothing in it, for a reason invisible in
 * both the markup and the props.
 *
 * So the four falsy shapes a conditional expression actually produces are treated as absent:
 * `null`, `undefined`, either boolean, and the empty string. An array is recursed, because
 * `{items.map(…)}` over an empty list and `[cond && <A/>, cond && <B/>]` are the same situation one
 * level down.
 *
 * ── `0` is NOT absent, and that is the whole reason this is not `!node` ──────────────────────
 *
 * `0` is a legitimate thing to render — "0 responses", a count, a duration — and it is falsy. A
 * `!node` guard would silently swallow it, which is the classic React rendering bug and the one
 * this helper exists to avoid reintroducing. `''` is excluded and `0` is not, deliberately: an
 * empty string is the absence of text, a zero is a value.
 *
 * ── What it CANNOT see, which is documented rather than worked around ────────────────────────
 *
 * A React ELEMENT is opaque. `<></>`, `<Foo/>` where `Foo` returns `null`, and a component that
 * renders only whitespace are all indistinguishable from an element with content, because
 * answering the question would mean rendering it. Those stay the caller's responsibility, and the
 * props docs say so. There is no way around it short of a ref-measuring effect, which would move
 * a layout decision into a second paint.
 */
const isEmptyNode = (node: ReactNode): boolean => {
  if (node === null || node === undefined || typeof node === 'boolean' || node === '') return true;

  return Array.isArray(node) && node.every((child) => isEmptyNode(child as ReactNode));
};

/**
 * Design System 2.0 content block — a static titled grouping surface.
 *
 * Ported from Figma `1505:28837` on 2026-09-22, and reworked the same day when the set's `Surface`
 * axis collapsed from three values to two. Three variants, two axes, and no interactive state of
 * any kind: a content block never opens, never hovers and never disables. `Accordion` is the
 * component for content that opens and closes, and `ContentPanel` is the bare untitled box.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer in
 * `components/styles/design-system.css`, so the same markup is correct in both themes and
 * `.ds-content-block` needs no dark rule to achieve it.
 *
 * ── What it is FOR, next to `ContentPanel` ────────────────────────────────────────────────────
 *
 * The difference is not decoration. `ContentPanel` renders a plain `<div>` with no role, which is
 * correct for a box: a region with no accessible name is not exposed as a region at all, so a
 * built-in landmark there would be silently inert on most panels. This component has a REQUIRED
 * heading, so it can do the thing the panel deliberately refuses — it is always a named region,
 * with no caller-managed ids.
 *
 * That is the whole reason both exist. If you find yourself giving a `ContentPanel` a manual
 * heading id, a manual `<h2>`, a `role="region"` and an `aria-labelledby`, you want this instead;
 * the two Natalie panels this component was written for do exactly that today, four lines each.
 *
 * ── The accessible name, and why the id is not a prop ─────────────────────────────────────────
 *
 * A `<section>` is a `region` landmark only when it has an accessible name, so the heading's id is
 * generated with `useId` and the section points at it with `aria-labelledby`. Neither is exposed.
 *
 * A `titleId` prop would let a caller point the section at something other than the heading a
 * sighted user reads — the WCAG 2.5.3 mismatch — and would make every call site carry a `useId` of
 * its own. `aria-label` and `aria-labelledby` are both stripped from the props type for the same
 * reason, and `title` is stripped because it is the native tooltip attribute and this component
 * redefines the word.
 *
 * `useId` is React 18's, so the id is stable across a server render and a hydration, and unique per
 * instance — which matters in a story or a theme sheet that renders the same block twice.
 *
 * ── Two invariants that throw ─────────────────────────────────────────────────────────────────
 *
 * Both are in the props type as well; these are the runtime half, for a value that arrived from an
 * API, a cast, a spread or a JavaScript caller with the types erased.
 *
 *   1. A blank or whitespace-only `title`. An empty heading is worse than a missing one: it
 *      announces a region whose name is the empty string, and it passes every type check.
 *   2. `layout="stacked"` with `surface="none"`. The set draws three cells and its description says
 *      "Surface=None × Stacked does not exist". Coercing it to `inline` would be worse than
 *      throwing — the caller asked for something specific and would get something else silently.
 *
 * Thrown rather than logged, because both are programming errors with a one-line fix and neither
 * has a sensible fallback. A `logger.error` plus a best guess is how a wrong variant reaches
 * production looking deliberate.
 *
 * ── How the defaults resolve ──────────────────────────────────────────────────────────────────
 *
 * Not one default per prop. Omitting both gives `filled + stacked`, which is the set's own default
 * cell. But `surface="none"` with no layout resolves to `inline`, because `stacked` is not
 * available there — a single `defaultVariants: { layout: 'stacked' }` in the recipe would make
 * `<ContentBlock surface="none" title="…" />`, the shortest possible no-surface call, throw on its
 * own default. So the layout default is derived from the surface here, and the recipe's own default
 * only covers the case where neither is given.
 *
 * ── Forwarded props are spread BEFORE the internal markers ────────────────────────────────────
 *
 * `{...rest}` sits above `data-surface`, `data-layout`, `data-part` and `aria-labelledby` in the
 * JSX, so a caller cannot overwrite any of them. That ordering is the assertion: a `data-surface`
 * passed in would otherwise change what the stylesheet paints without changing what the recipe
 * drew, leaving a block with one surface's geometry and another's colour. The unit tests attempt
 * each override explicitly rather than trusting the read.
 */
export const ContentBlock = forwardRef<HTMLElement, ContentBlockProps>(
  ({ children, className, description, footer, headingLevel = 2, layout, surface = 'filled', title, ...rest }, ref) => {
    if (title.trim() === '') {
      throw new Error('ContentBlock requires a non-empty title.');
    }

    if (surface === 'none' && layout === 'stacked') {
      throw new Error('ContentBlock does not support layout="stacked" with surface="none".');
    }

    // Read off the surface rather than defaulted in the signature — see the note above on why a
    // single default would make the shortest no-surface call throw.
    const resolvedLayout = layout ?? (surface === 'none' ? 'inline' : 'stacked');

    const headingId = useId();
    // A template rather than a lookup table: the level is constrained to 1-6 by the props type and
    // again by the runtime shape of the token layer, so there is no invalid tag this can produce.
    const Heading = `h${headingLevel}` as const;

    const hasDescription = !isEmptyNode(description);
    const hasContent = !isEmptyNode(children);
    const hasFooter = !isEmptyNode(footer);

    return (
      <section
        ref={ref}
        className={cn(contentBlockRootRecipe({ layout: resolvedLayout, surface }), className)}
        {...rest}
        aria-labelledby={headingId}
        data-layout={resolvedLayout}
        data-part="root"
        data-surface={surface}
      >
        <div className={contentBlockHeaderRecipe()} data-part="header">
          <Heading id={headingId} className={contentBlockTitleRecipe()} data-part="title">
            {title}
          </Heading>

          {hasDescription && (
            <p className={contentBlockDescriptionRecipe()} data-part="description">
              {description}
            </p>
          )}
        </div>

        {hasContent && (
          <div className={contentBlockContentRecipe()} data-part="content">
            {children}
          </div>
        )}

        {hasFooter && (
          <div className={contentBlockFooterRecipe()} data-part="footer">
            {footer}
          </div>
        )}
      </section>
    );
  }
);
